/**
 * A door: a kinematic leaf swinging on a hinge at one jamb, plus a fixed sensor
 * filling the doorway.
 *
 * The store owns whether the door is open or closed; this component owns the swing.
 * The leaf animates through refs in `useFrame` — no React state per frame. The sensor
 * counts the player and carryable boxes into the store's occupancy map (discrete
 * enter/leave events), which is what lets a close be refused and a mid-swing close
 * reverse instead of crushing what is in the doorway.
 */

import { useFrame } from '@react-three/fiber';
import { CuboidCollider, RigidBody, useRapier, type RapierRigidBody } from '@react-three/rapier';
import { useRef } from 'react';
import { BackSide, Quaternion, Vector3 } from 'three';
import { isPlayable } from '../game/phase';
import type { Doorway } from '../game/profile/buildGeometry';
import { useGameStore } from '../store/gameStore';
import { getGradientMap } from './materials';
import type { BodyUserData } from './useInteractionTarget';

const LEAF_THICKNESS = 0.08;
/** Radians per second; a swing takes a bit over half a second. */
const SWING_SPEED = 3.2;
/** A little past a right angle, so an open door rests against the wall. */
const OPEN_ANGLE = 1.9;
/** How deep (across the wall) the doorway sensor watches, in metres. */
const SENSOR_DEPTH = 0.9;

const UP = new Vector3(0, 1, 0);

type Props = {
  doorway: Doorway;
  color: string;
};

function isOccupant(data: unknown): boolean {
  const kind = (data as Partial<BodyUserData> | undefined)?.kind;
  return kind === 'player' || kind === 'carryable';
}

/** Near the free edge of the leaf, at hand height, poking out both faces. */
function handleOffset(axis: 'x' | 'z', width: number, height: number): [number, number, number] {
  const alongLeaf = width - 0.15;
  const atHandHeight = 1 - height / 2;
  return axis === 'x' ? [0, atHandHeight, alongLeaf] : [alongLeaf, atHandHeight, 0];
}

function handleSize(axis: 'x' | 'z'): [number, number, number] {
  const across = LEAF_THICKNESS + 0.14;
  return axis === 'x' ? [across, 0.07, 0.07] : [0.07, 0.07, across];
}

export function Door({ doorway, color }: Props) {
  const initialAngle = doorway.isInitiallyOpen ? OPEN_ANGLE : 0;
  const bodyRef = useRef<RapierRigidBody>(null);
  const angleRef = useRef(initialAngle);
  const rotationRef = useRef(new Quaternion());
  const { rapier } = useRapier();
  // Discrete re-render on gaining/losing the crosshair, never per frame.
  const isFocused = useGameStore((store) => store.focus?.id === doorway.id);

  const { axis, center, width, height } = doorway;
  // Hinge at one jamb; the leaf extends from it along the positive axis, so rotating
  // the body about the hinge swings the whole leaf.
  const hinge: [number, number, number] =
    axis === 'x' ? [center.x, center.y, center.z - width / 2] : [center.x - width / 2, center.y, center.z];
  const leafOffset: [number, number, number] =
    axis === 'x' ? [0, 0, width / 2] : [width / 2, 0, 0];
  const leafSize: [number, number, number] =
    axis === 'x' ? [LEAF_THICKNESS, height, width] : [width, height, LEAF_THICKNESS];
  const sensorSize: [number, number, number] =
    axis === 'x' ? [SENSOR_DEPTH, height, width] : [width, height, SENSOR_DEPTH];

  useFrame((_, delta) => {
    const body = bodyRef.current;
    if (!body) return;

    const store = useGameStore.getState();
    if (!isPlayable(store.phase)) return;

    const target = (store.doorStates[doorway.id] ?? 'closed') === 'open' ? OPEN_ANGLE : 0;
    const current = angleRef.current;
    if (current === target) return;

    // Something moved into the doorway while the leaf was closing: reverse. Discrete
    // and rare — this is an event, not a per-frame write.
    if (target === 0 && (store.doorwayOccupancy[doorway.id] ?? 0) > 0) {
      store.forceDoorOpen(doorway.id);
      return;
    }

    const step = SWING_SPEED * Math.min(delta, 0.1);
    angleRef.current =
      current < target ? Math.min(current + step, target) : Math.max(current - step, target);
    body.setNextKinematicRotation(rotationRef.current.setFromAxisAngle(UP, angleRef.current));
  });

  return (
    <>
      <RigidBody
        ref={bodyRef}
        type="kinematicPosition"
        colliders={false}
        position={hinge}
        rotation={[0, initialAngle, 0]}
        userData={{ kind: 'door', id: doorway.id } satisfies BodyUserData}
      >
        <CuboidCollider
          args={[leafSize[0] / 2, leafSize[1] / 2, leafSize[2] / 2]}
          position={leafOffset}
        />
        <mesh position={leafOffset} castShadow>
          <boxGeometry args={leafSize} />
          <meshToonMaterial
            color={color}
            gradientMap={getGradientMap()}
            // Focus feedback is brightness plus the outline and prompt — not hue alone.
            emissive={isFocused ? color : '#000000'}
            emissiveIntensity={isFocused ? 0.3 : 0}
          />
        </mesh>
        {/* The handle — the shape cue that says "door you can use", at hand height. */}
        <mesh position={handleOffset(axis, width, height)} castShadow>
          <boxGeometry args={handleSize(axis)} />
          <meshToonMaterial color="#3d3428" gradientMap={getGradientMap()} />
        </mesh>
        {isFocused && (
          <mesh position={leafOffset} scale={1.05}>
            <boxGeometry args={leafSize} />
            <meshBasicMaterial color="#ffffff" side={BackSide} />
          </mesh>
        )}
      </RigidBody>

      <CuboidCollider
        sensor
        args={[sensorSize[0] / 2, sensorSize[1] / 2, sensorSize[2] / 2]}
        position={[center.x, center.y, center.z]}
        // The player capsule is kinematic; a fixed sensor ignores kinematic bodies
        // unless told otherwise.
        activeCollisionTypes={
          rapier.ActiveCollisionTypes.DEFAULT | rapier.ActiveCollisionTypes.KINEMATIC_FIXED
        }
        onIntersectionEnter={({ other }) => {
          if (isOccupant(other.rigidBody?.userData)) {
            useGameStore.getState().reportDoorwayEntered(doorway.id);
          }
        }}
        onIntersectionExit={({ other }) => {
          if (isOccupant(other.rigidBody?.userData)) {
            useGameStore.getState().reportDoorwayLeft(doorway.id);
          }
        }}
      />
    </>
  );
}
